import { useNavigate } from "react-router-dom";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { Skeleton } from "@/components/Skeleton";
import { BottomSheet } from "@/components/BottomSheet";
import { ShibaTeacher } from "@/features/shiba-teacher";
import { useCoachToday, type CoachAction } from "@/api/queries/coach";
import { useLearningPreferences } from "@/api/queries/learning";
import { useStartWeaknessSessionMutation } from "@/api/mutations/weaknessSession";
import { useUiStore } from "@/stores/uiStore";
import { track } from "@/lib/analytics";

const ACTION_PATH: Record<Exclude<CoachAction, "later" | "weakness">, string> = {
  review: "/app/review/srs",
  lesson: "/app/practice",
  diagnosis: "/xhs-diagnosis",
  daily_mission: "/app/mission",
};

export function CoachSheet() {
  const open = useUiStore((s) => s.isCoachOpen);
  const setOpen = useUiStore((s) => s.setCoachOpen);
  const pushToast = useUiStore((s) => s.pushToast);
  const navigate = useNavigate();
  const { data, isLoading } = useCoachToday();
  const {
    data: learningPreferences,
    isLoading: learningPreferencesLoading,
  } = useLearningPreferences();
  const startWeakness = useStartWeaknessSessionMutation();

  const handleAction = async (action: CoachAction) => {
    if (action === "daily_mission" && data?.daily_mission) {
      track("app_home.daily_mission_start", {
        run_id: data.daily_mission.run_id,
      });
    }
    if (action === "later") {
      setOpen(false);
      return;
    }
    if (action === "weakness") {
      setOpen(false);
      if (!data?.weakness_target) {
        navigate("/app/review");
        return;
      }
      try {
        const resp = await startWeakness.mutateAsync({
          group_key: data.weakness_target.key,
        });
        track("review.tag_drill_start", {
          group_key: data.weakness_target.key,
          count: resp.question_ids.length,
          entry: "coach",
        });
        navigate(`/app/review/session/${resp.session_id}`, {
          state: { questionIds: resp.question_ids },
        });
      } catch {
        pushToast("启动练习失败，请稍后再试", "error");
      }
      return;
    }
    if (action === "lesson") {
      if (learningPreferencesLoading) {
        pushToast("正在准备题包，请稍候", "info");
        return;
      }
      const packId = learningPreferences?.current_pack_id ?? null;
      setOpen(false);
      navigate(packId ? `/app/practice?pack=${packId}` : ACTION_PATH.lesson);
      return;
    }
    setOpen(false);
    navigate(ACTION_PATH[action]);
  };

  return (
    <BottomSheet open={open} onClose={() => setOpen(false)} ariaLabel="小柴老师">
      <div className="px-6 pb-8 flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <ShibaTeacher size={72} expression="happy" />
          <Card className="flex-1 bg-sky/10">
            {isLoading || !data ? (
              <>
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="mt-2 h-4 w-2/3" />
              </>
            ) : (
              <>
                <p className="font-bold text-sumi">{data.message}</p>
                <p className="text-xs text-sumi-soft mt-1">{data.reason_text}</p>
              </>
            )}
          </Card>
        </div>

        {data?.daily_mission && data.recommended_action === "daily_mission" && (
          <Card className="bg-bamboo-mist">
            <p className="font-bold text-sumi">
              {data.daily_mission.icon_emoji
                ? `${data.daily_mission.icon_emoji} `
                : ""}
              {data.daily_mission.title}
            </p>
            {data.daily_mission.opening_copy && (
              <p className="text-sm text-sumi-soft mt-1">
                {data.daily_mission.opening_copy}
              </p>
            )}
          </Card>
        )}

        {data && (
          <div className="flex flex-col gap-2">
            {/* Show only the head recommendation's button so the sheet stays
                focused; the queue still advances as each action completes. The
                idle "all done" state (recommended_action === "later") keeps its
                fallback buttons. */}
            {(data.recommended_action === "later"
              ? data.buttons
              : data.buttons.filter(
                  (btn) => btn.action === data.recommended_action,
                )
            ).map((btn) => (
              <Button
                key={btn.action}
                variant={btn.action === data.recommended_action ? "primary" : "ghost"}
                size="md"
                fullWidth
                onClick={() => handleAction(btn.action)}
              >
                {btn.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
